<template>
  <div class="w-full h-8 px-4 box-border flex items-center rounded-2xl audio_player">
    <span class="text-xs text-theme">{{ "当前播放：" + fn }}&nbsp;</span>
    <i
      class="text-medium-x text-theme cursor-pointer"
      :class="isPlay ? 'el-icon-video-pause' : 'el-icon-video-play'"
      @click="playHandle"
    ></i>
    <span class="mx-2 whitespace-nowrap text-small text-theme-black-450">
      {{ currentDuration }}&nbsp;/&nbsp;{{ totalDuration }}
    </span>
    <div class="flex-1 mx-1 box-border audio_slider">
      <el-slider
        :min="0"
        :max="maxDuration"
        :step="0.1"
        :show-tooltip="false"
        v-model="playSlider"
        @change="changePlaySlider"
      ></el-slider>
    </div>
    <span class="ml-2 text-small text-theme">{{ speedText }}</span>
    <el-popover placement="top" width="32" trigger="hover" popper-class="audio_volume">
      <i
        class="ml-2 text-medium-x text-theme cursor-pointer"
        :class="muted || volume === 0 ? 'el-icon-turn-off-microphone' : 'el-icon-microphone'"
        slot="reference"
        @click="setVolumeHandle"
      ></i>
      <div class="w-8 pt-4 pb-3 flex flex-col items-center audio_slider">
        <el-slider
          vertical
          :min="0"
          :max="100"
          height="75px"
          :show-tooltip="false"
          v-model="volume"
          @change="changeVolume"
          @input="inuptVolume"
        ></el-slider>
      </div>
    </el-popover>
    <el-dropdown>
      <i class="ml-2 text-medium-x text-theme el-icon-more transform rotate-90 cursor-pointer"></i>
      <el-dropdown-menu slot="dropdown">
        <el-dropdown-item>
          <div class="flex items-center" style="width: 150px">
            <span class="mr-2 text-small text-theme-black-850 whitespace-nowrap">播放速度：</span>
            <div class="flex-1 audio_slider">
              <el-slider :min="0.5" :max="4" :step="0.1" :show-tooltip="false" v-model="speed" @input="changeSpeed"></el-slider>
            </div>
          </div>
        </el-dropdown-item>
        <el-dropdown-item>
          <span class="text-small text-theme-black-850 cursor-pointer" @click="closeAudioHandle">关闭播放器</span>
        </el-dropdown-item>
      </el-dropdown-menu>
    </el-dropdown>
  </div>
</template>

<script>
export default {
  name: 'AudioPlayer',
  props: {
    src: {
      type: String,
      default: ''
    },
    fn: { // filename
      type: String,
      default: ''
    },
    autoPlay: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const audio = new Audio()
    return {
      audio,
      playSlider: 0,
      volume: 100,
      preVolume: 100,
      speed: 1,
      duration: null,
      muted: audio.muted,
      isPlay: false
    }
  },
  computed: {
    speedText() {
      return `${(+this.speed).toFixed(1)}X`
    },
    totalDuration() {
      if (this.duration === null) {
        return '0:00'
      }
      const text = this.getFormatDuration(this.duration)
      return text
    },
    maxDuration() {
      if (this.duration === null) return 100
      return this.duration
    },
    currentDuration() {
      if (this.playSlider === 0) return '0:00'
      return this.getFormatDuration(this.playSlider)
    }
  },
  watch: {
    src(n, o) {
      if (o) {
        this.reload()
      } else {
        this.setURL()
      }
    }
  },
  beforeDestroy() {
    this.clearAudio()
  },
  mounted() {
    this.setURL()
    this.audio.load()
    this.audio.addEventListener('timeupdate', this.playingHandler)
    this.audio.addEventListener('ended', this.endedHandler)
    this.audio.addEventListener('loadeddata', this.loadHandler)
  },
  methods: {
    getFormatDuration(duration = 0) {
      const totalMinute = Math.floor(duration / 60)
      const hour = Math.floor(totalMinute / 60)
      const second = Math.floor(duration % 60)
      const minute = Math.floor(totalMinute % 60)
      return `${hour > 0 ? `${hour}:` : ''}${minute >= 10 ? minute : `0${minute}`}:${second >= 10 ? second : `0${second}`}`
    },
    play() {
      this.audio.play()
      this.isPlay = true
    },
    pause() {
      this.audio.pause()
      this.isPlay = false
    },
    setURL() {
      this.audio.src = this.src
    },
    reload() {
      this.pause()
      this.setURL()
      this.audio.load()
    },
    setDuration() {
      const duration = this.audio.duration
      this.duration = Object.is(duration, NaN) || Object.is(duration, Infinity) ? null : +duration.toFixed(3)
    },
    playingHandler() {
      this.playSlider = +(+this.audio.currentTime).toFixed(1)
    },
    changePlaySlider() {
      this.audio.currentTime = this.playSlider
    },
    changeVolume() {
      if (this.muted) {
        this.muted = false
        this.audio.muted = false
      } else {
        this.preVolume = this.volume
      }
    },
    inuptVolume() {
      this.audio.volume = +(this.volume / 100).toFixed(1)
    },
    changeSpeed() {
      this.audio.playbackRate = this.speed
    },
    endedHandler() {
      this.isPlay = false
      this.playSlider = 0
    },
    loadHandler() {
      this.setDuration()
      if (this.autoPlay) {
        this.play()
      }
    },
    clearAudio() {
      if (this.audio) {
        this.pause()
        this.audio.src = ''
        this.audio.load()
        this.playSlider = 0
        this.volume = 100
        this.speed = 1
        this.duration = null
        this.muted = false
        this.isPlay = false
        this.audio.removeEventListener('timeupdate', this.playingHandler)
        this.audio.removeEventListener('ended', this.endedHandler)
        this.audio.removeEventListener('loadeddata', this.loadHandler)
        this.audio = null
      }
    },
    playHandle() {
      if (this.isPlay) {
        this.pause()
      } else {
        this.play()
      }
    },
    setVolumeHandle() {
      this.audio.muted = !this.audio.muted
      this.muted = this.audio.muted
      if (this.muted) {
        this.volume = 0
      } else {
        this.volume = this.preVolume
      }
    },
    closeAudioHandle() {
      this.clearAudio()
      this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
.audio_player {
  @apply bg-theme bg-opacity-10;
}
.audio_slider {
  ::v-deep .el-slider {
    .el-slider__button-wrapper {
      top: -16px;
    }
    .el-slider__button {
      width: 8px;
      height: 8px;
      @apply border-theme bg-theme;
    }
    .el-slider__bar {
      height: 4px;
      @apply bg-theme;
    }
    .el-slider__runway {
      height: 4px;
      background-color: #d8d8d8;
    }
    &.is-vertical {
      .el-slider__button-wrapper {
        top: auto;
        left: -16px;
      }
      .el-slider__bar {
        height: auto;
        width: 4px;
      }
      .el-slider__runway {
        height: 100%;
        width: 4px;
      }
    }
  }
}
</style>

<style lang="scss">
.audio_volume {
  padding: 0;
  min-width: auto;
  .popper__arrow {
    display: none;
  }
}
</style>
